import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NotificationProvider } from '@mysimpledb/shared/context/NotificationContext';
import NotificationDisplay from '@mysimpledb/shared/context/NotificationDisplay';
import { Routes, Route, Navigate } from 'react-router-dom';
import Home from './components/Home';

import MySimpleDBFooter from './components/mysimpledb/Footer';
import MySimpleDBPrivacy from './pages/mysimpledb/legal/Privacy';
import MySimpleDBTerms from './pages/mysimpledb/legal/Terms';
import MySimpleDBSmsTerms from './pages/mysimpledb/legal/SmsTerms';
import MySimpleDBHelpSupport from './pages/mysimpledb/HelpSupport';
import MySimpleDBContactUs from './pages/mysimpledb/ContactUs';
import MySimpleDBAbout from './pages/mysimpledb/legal/About';

import MySimpleDBNotesFooter from './components/mysimpledbnotes/Footer';
import MySimpleDBNotesPrivacy from './pages/mysimpledbnotes/legal/Privacy';
import MySimpleDBNotesTerms from './pages/mysimpledbnotes/legal/Terms';
import MySimpleDBNotesSmsTerms from './pages/mysimpledbnotes/legal/SmsTerms';
import MySimpleDBNotesHelpSupport from './pages/mysimpledbnotes/HelpSupport';
import MySimpleDBNotesContactUs from './pages/mysimpledbnotes/ContactUs';
import MySimpleDBNotesAbout from './pages/mysimpledbnotes/legal/About';

import NotesHome from './components/NotesHome';
import LoginForm from './components/LoginForm';
//import CreateUserForm from './components/CreateUserForm';
import CreateAccount from './components/Account/CreateAccount';
//import UserData from './components/UserData';
import Account from './components/Account/Account';
import BillingSuccess from './components/Account/BillingSuccess';
import TwoFAChallengePage from './components/Security/TwoFactorChallengePage';
import VerifySuccess from './pages/VerifySuccess';
import EmailNotVerified from './pages/EmailNotVerified';

import RegistrationComplete from './pages/mysimpledb/legal/RegistrationComplete';

// import Settings from './components/Settings';
import Nav from './components/Nav';
import PublicNav from './components/PublicNav';
import ResetPassword from './components/ResetPassword';
import ForgotPassword from './components/ForgotPassword';
import { useNavigate } from 'react-router-dom';
import MyDataTabsWeb from './components/MyData/MyDataTabsWeb';
import MyNotesTabsWeb from './components/MyNotes/MyNotesTabsWeb';
import MessagesWeb from './components/Messages/MessagesWeb';
import { fetchUnreadMessageCount } from './api/messagingClient';
import useAuthTokenChecker from '@mysimpledb/shared/hooks/useAuthTokenChecker';
import useTokenMonitor from '@mysimpledb/shared/hooks/useTokenMonitor';
import { UserSettingsProvider } from '@mysimpledb/shared/context/UserSettingsContext';
import { Toaster, toast } from 'react-hot-toast';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import axios, { registerOnUnauthorized } from '@mysimpledb/shared/config/axios';
import { useVault } from '@mysimpledb/shared/context/VaultContext';
import {
  registerOnSessionExpired,
  scheduleSessionExpiryTimer,
  clearSessionExpiryTimer,
} from '@mysimpledb/shared/utils/sessionExpiry';
import DesktopSyncProcessor from './components/DesktopSyncProcessor';
import Downloads from './pages/downloads/Downloads';
import { useLocation } from 'react-router-dom';
import MobileAppLanding from './components/MobileAppLanding';

function getUserIdFromStoredToken() {
  const token = localStorage.getItem('token');
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.userId ?? null;
  } catch {
    localStorage.removeItem('token');
    return null;
  }
}

function WebApp() {
  useAuthTokenChecker();

  const [userId, setUserId] = useState(getUserIdFromStoredToken);
  const [authReady, setAuthReady] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [attachmentSearchTerm, setAttachmentSearchTerm] = useState('');
  const [myDataSearchMode, setMyDataSearchMode] = useState('entries');
  const [myDataEntryScope, setMyDataEntryScopeState] = useState(() => {
    try {
      return sessionStorage.getItem('myDataEntryScope') === 'mine'
        ? 'mine'
        : 'all';
    } catch {
      return 'all';
    }
  });
  const [searchType, setSearchType] = useState('');
  const [userTheme, setUserTheme] = useState('light');
  const navigate = useNavigate();
  const location = useLocation();

  const isNotesSite =
    window.location.hostname === 'mysimpledbnotes.com' ||
    window.location.hostname === 'www.mysimpledbnotes.com';

  const [userProfile, setUserProfile] = useState(null);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const unreadRequestStateRef = useRef({ userId: null, inFlight: false });
  const { lockVault, resetAutoLockTimer } = useVault();

  const authToken = userId ? localStorage.getItem('token') : null;
  const productCode = userId ? localStorage.getItem('productCode') : null;

  const hasNotes = productCode === 'notes' || productCode === 'mysimpledb';
  const hasMySimpleDB = productCode === 'mysimpledb';

  const [myDataSearchPlaceholder, setMyDataSearchPlaceholder] =
    useState('Search Entries');

  const isMyDataPath = location.pathname.startsWith('/mydata');
  const isAttachmentSearchMode =
    isMyDataPath && myDataSearchMode === 'attachments';

  const navSearchTerm = isAttachmentSearchMode
    ? attachmentSearchTerm
    : searchTerm;

  const searchPlaceholder = location.pathname.startsWith('/mynotes')
    ? 'Search Notes'
    : myDataSearchPlaceholder;

  const handleNavSearchTermChange = (value) => {
    if (isAttachmentSearchMode) {
      setAttachmentSearchTerm(value);
      return;
    }

    setSearchTerm(value);
  };

  const handleNavSearchTypeChange = (value) => {
    if (isAttachmentSearchMode) return;
    setSearchType(value);
  };

  const refreshUnreadMessageCount = useCallback(async () => {
    if (!userId || !hasMySimpleDB || isNotesSite) return;

    const requestUserId = String(userId);
    const requestState = unreadRequestStateRef.current;
    if (requestState.inFlight && requestState.userId === requestUserId) return;

    unreadRequestStateRef.current = {
      userId: requestUserId,
      inFlight: true,
    };
    try {
      const count = await fetchUnreadMessageCount();
      if (unreadRequestStateRef.current.userId === requestUserId) {
        setUnreadMessageCount(count);
      }
    } catch {
      // Keep the last known count when a background refresh fails. The shared
      // authentication handler will still process an expired session.
    } finally {
      if (unreadRequestStateRef.current.userId === requestUserId) {
        unreadRequestStateRef.current.inFlight = false;
      }
    }
  }, [userId, hasMySimpleDB, isNotesSite]);

  const setMyDataEntryScope = (nextScope) => {
    const normalizedScope = nextScope === 'mine' ? 'mine' : 'all';
    setMyDataEntryScopeState(normalizedScope);

    try {
      sessionStorage.setItem('myDataEntryScope', normalizedScope);
    } catch (e) {
      console.warn('Unable to store MyData entry scope', e);
    }
  };

  const handleLogout = () => {
    lockVault();
    setMyDataEntryScopeState('all');
    setSearchTerm('');
    setAttachmentSearchTerm('');
    setMyDataSearchMode('entries');
    setSearchType('');
    setUserProfile(null);
    setUnreadMessageCount(0);
    unreadRequestStateRef.current = { userId: null, inFlight: false };
    clearSessionExpiryTimer();
    localStorage.removeItem('token');
    localStorage.removeItem('userId');

    let siteSecret = null;
    try {
      siteSecret = sessionStorage.getItem('mysdb_site_secret');
    } catch (e) {
      siteSecret = null;
    }

    try {
      sessionStorage.clear();
    } catch (e) {
      console.warn('Unable to clear sessionStorage', e);
    }

    try {
      localStorage.clear();
    } catch (e) {
      console.warn('Unable to clear localStorage', e);
    }

    if (siteSecret) {
      try {
        sessionStorage.setItem('mysdb_site_secret', siteSecret);
      } catch (e) {
        console.warn('Unable to restore site gate secret', e);
      }
    }

    setUserId(null);
  };

  useTokenMonitor({ onLogout: handleLogout });

  useEffect(() => {
    const activityEvents = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    const handleActivity = () => {
      resetAutoLockTimer();
    };

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
    };
  }, [resetAutoLockTimer]);

  useEffect(() => {
    registerOnUnauthorized(() => {
      lockVault();

      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      window.location.href = '/';
    });
  }, [lockVault]);

  useEffect(() => {
    registerOnSessionExpired(() => {
      lockVault();

      localStorage.removeItem('token');
      window.location.href = '/';
    });
  }, [lockVault]);

  useEffect(() => {
    setUserId(getUserIdFromStoredToken());
    setAuthReady(true);
  }, []);

  useEffect(() => {
    const fetchMe = async () => {
      if (!userId) {
        setUserProfile(null);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setUserProfile(null);
          return;
        }

        const { data } = await axios.get(`/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setUserProfile({
          firstName: data.fname ?? '',
          lastName: data.lname ?? '',
          email: data.email ?? '',
        });
      } catch (e) {
        console.warn('Failed to load user profile', e);
        setUserProfile(null);
      }
    };

    fetchMe();
  }, [userId]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', userTheme);
  }, [userTheme]);

  useEffect(() => {
    if (!userId) {
      setUnreadMessageCount(0);
      return undefined;
    }

    refreshUnreadMessageCount();

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        refreshUnreadMessageCount();
      }
    }, 30000);

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshUnreadMessageCount();
      }
    };

    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [refreshUnreadMessageCount, userId]);

  return (
    <>
      <Toaster />

      <Nav
        userId={userId}
        productCode={productCode}
        userFirstName={userProfile?.firstName ?? ''}
        userLastName={userProfile?.lastName ?? ''}
        userEmail={userProfile?.email ?? ''}
        searchTerm={navSearchTerm}
        setSearchTerm={handleNavSearchTermChange}
        setSearchType={handleNavSearchTypeChange}
        searchPlaceholder={searchPlaceholder}
        entryScope={myDataEntryScope}
        setEntryScope={setMyDataEntryScope}
        handleLogout={handleLogout}
        unreadMessageCount={unreadMessageCount}
      />

      <DesktopSyncProcessor token={authToken} userId={userId} />
      <NotificationProvider>
        <UserSettingsProvider token={authToken}>
          <NotificationDisplay />

          <Routes>
            <Route
              path="/"
              element={
                isNotesSite ? (
                  <>
                    <NotesHome userId={userId} />
                    <MySimpleDBNotesFooter />
                  </>
                ) : (
                  <>
                    <Home setUserId={setUserId} />
                    <MySimpleDBFooter />
                  </>
                )
              }
            />

            <Route
              path="/create-account"
              element={
                userId ? (
                  <Navigate to="/" />
                ) : (
                  <CreateAccount
                    onSuccess={() => navigate('/verify-success')}
                  />
                )
              }
            />

            <Route
              path="/mydata"
              element={
                !authReady ? null : !userId ? (
                  <Navigate to="/" replace />
                ) : !hasMySimpleDB || isNotesSite ? (
                  <Navigate to="/mynotes" replace />
                ) : (
                  <MyDataTabsWeb
                    userId={userId}
                    searchTerm={searchTerm}
                    searchType={searchType}
                    attachmentSearchTerm={attachmentSearchTerm}
                    setAttachmentSearchTerm={setAttachmentSearchTerm}
                    setSearchTerm={setSearchTerm}
                    setSearchType={setSearchType}
                    setMyDataSearchPlaceholder={setMyDataSearchPlaceholder}
                    setMyDataSearchMode={setMyDataSearchMode}
                    entryScope={myDataEntryScope}
                  />
                )
              }
            />

            <Route
              path="/mynotes"
              element={
                !authReady ? null : userId ? (
                  <MyNotesTabsWeb
                    userId={userId}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    setSearchType={setSearchType}
                  />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            <Route
              path="/messages"
              element={
                !authReady ? null : !userId ? (
                  <Navigate to="/login" replace />
                ) : !hasMySimpleDB || isNotesSite ? (
                  <Navigate to="/mynotes" replace />
                ) : (
                  <MessagesWeb
                    userId={userId}
                    onUnreadCountRefresh={refreshUnreadMessageCount}
                  />
                )
              }
            />

            <Route
              path="/account"
              element={userId ? <Account /> : <Navigate to="/login" />}
            />

            <Route
              path="/account/people-sharing"
              element={
                userId ? <Account atab="sharing" /> : <Navigate to="/login" />
              }
            />

            <Route
              path="/account/profile"
              element={
                userId ? <Account atab="profile" /> : <Navigate to="/login" />
              }
            />

            <Route
              path="/account/public-profile"
              element={
                userId ? (
                  <Account atab="public-profile" />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/account/usage"
              element={
                userId ? <Account atab="usage" /> : <Navigate to="/login" />
              }
            />

            <Route
              path="/account/settings"
              element={
                userId ? <Account atab="settings" /> : <Navigate to="/login" />
              }
            />

            <Route
              path="/account/billing"
              element={
                userId ? <Account atab="billing" /> : <Navigate to="/login" />
              }
            />

            <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/email-not-verified" element={<EmailNotVerified />} />

            <Route
              path="/account/security/:sub"
              element={userId ? <Account /> : <Navigate to="/login" />}
            />

            <Route
              path="/login"
              element={<LoginForm setUserId={setUserId} />}
            />

            <Route
              path="/forgot-password"
              element={userId ? <Navigate to="/" replace /> : <ForgotPassword />}
            />

            <Route
              path="/reset-password"
              element={userId ? <Navigate to="/" replace /> : <ResetPassword />}
            />

            <Route
              path="/auth/2fa"
              element={<TwoFAChallengePage setUserId={setUserId} />}
            />

            <Route path="/verify-success" element={<VerifySuccess />} />
            <Route path="/downloads" element={<Downloads />} />

            <Route
              path="/privacy"
              element={
                isNotesSite ? <MySimpleDBNotesPrivacy /> : <MySimpleDBPrivacy />
              }
            />

            <Route
              path="/terms"
              element={
                isNotesSite ? <MySimpleDBNotesTerms /> : <MySimpleDBTerms />
              }
            />

            <Route
              path="/sms-terms"
              element={
                isNotesSite ? (
                  <MySimpleDBNotesSmsTerms />
                ) : (
                  <MySimpleDBSmsTerms />
                )
              }
            />

            <Route
              path="/about"
              element={
                isNotesSite ? <MySimpleDBNotesAbout /> : <MySimpleDBAbout />
              }
            />

            <Route
              path="/help"
              element={
                isNotesSite ? (
                  <MySimpleDBNotesHelpSupport />
                ) : (
                  <MySimpleDBHelpSupport />
                )
              }
            />

            <Route
              path="/contact"
              element={
                isNotesSite ? (
                  <MySimpleDBNotesContactUs />
                ) : (
                  <MySimpleDBContactUs />
                )
              }
            />

            <Route
              path="/registration-complete"
              element={<RegistrationComplete />}
            />
          </Routes>
        </UserSettingsProvider>
      </NotificationProvider>
    </>
  );
}

function getMobilePlatform() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return null;
  }

  const userAgent = navigator.userAgent || '';

  if (/iPhone|iPod/i.test(userAgent)) {
    return 'ios';
  }

  if (/Android/i.test(userAgent)) {
    return 'android';
  }

  if (window.innerWidth <= 600) {
    return 'mobile';
  }

  return null;
}

function App() {
  const [mobilePlatform, setMobilePlatform] = useState(getMobilePlatform);

  const [continueOnWebsite, setContinueOnWebsite] = useState(() => {
    try {
      return sessionStorage.getItem('mysdb_continue_web_on_phone') === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const handleResize = () => {
      setMobilePlatform(getMobilePlatform());
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleContinueToWebsite = () => {
    try {
      sessionStorage.setItem('mysdb_continue_web_on_phone', '1');
    } catch (e) {
      console.warn('Unable to remember mobile website choice', e);
    }

    setContinueOnWebsite(true);
  };

  if (mobilePlatform && !continueOnWebsite) {
    return (
      <MobileAppLanding
        platform={mobilePlatform}
        onContinue={handleContinueToWebsite}
      />
    );
  }

  return <WebApp />;
}

export default App;
